import {
  createFileRoute,
  Link,
  Outlet,
  useRouterState
} from '@tanstack/react-router';
import { Settings, User, Users, Boxes, Shield, Bot } from 'lucide-react';

import { useAuth } from '~/lib/auth';
import { requireSession } from '~/lib/route-auth';
import { cn } from '~/lib/utils';

export const Route = createFileRoute('/settings')({
  beforeLoad: async () => {
    await requireSession();
  },
  component: SettingsLayout
});

const navClass = (active: boolean) =>
  cn(
    'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
    active
      ? 'bg-accent text-accent-foreground'
      : 'text-muted-foreground hover:bg-accent/80 hover:text-accent-foreground'
  );

function SettingsLayout() {
  const { user } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const staffNav = user?.role === 'owner' || user?.role === 'admin';

  const generalActive = pathname === '/settings' || pathname === '/settings/';

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 p-4 md:gap-6 md:p-6 lg:flex-row lg:gap-10">
      <aside className="shrink-0 lg:w-52">
        <nav className="flex flex-row flex-wrap gap-1 lg:flex-col lg:gap-0.5">
          <Link to="/settings" className={navClass(generalActive)}>
            <Settings className="h-4 w-4 shrink-0 opacity-80" />
            General
          </Link>
          <Link
            to="/settings/profile"
            className={navClass(pathname === '/settings/profile')}
          >
            <User className="h-4 w-4 shrink-0 opacity-80" />
            Profile
          </Link>
          {staffNav ? (
            <>
              <Link
                to="/settings/users"
                className={navClass(pathname === '/settings/users')}
              >
                <Users className="h-4 w-4 shrink-0 opacity-80" />
                Users
              </Link>
              <Link
                to="/settings/groups"
                className={navClass(pathname === '/settings/groups')}
              >
                <Boxes className="h-4 w-4 shrink-0 opacity-80" />
                Groups
              </Link>
              <Link
                to="/settings/audit"
                className={navClass(pathname === '/settings/audit')}
              >
                <Shield className="h-4 w-4 shrink-0 opacity-80" />
                Audit logs
              </Link>
              <Link
                to="/settings/agent"
                className={navClass(pathname === '/settings/agent')}
              >
                <Bot className="h-4 w-4 shrink-0 opacity-80" />
                Agent
              </Link>
            </>
          ) : null}
        </nav>
      </aside>
      <div className="min-w-0 flex-1 max-w-6xl">
        <Outlet />
      </div>
    </div>
  );
}
